import React, { useState, useEffect, useCallback } from 'react';
import './Carousel.css';

const Carousel = ({ items, renderCard, isVisible }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(window.innerWidth > 900 ? 2 : 1);
  const autoRotateInterval = 5000; // 5 seconds

  // Handle responsive items per page
  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(window.innerWidth > 900 ? 2 : 1);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  // Auto-rotation logic
  useEffect(() => {
    let interval;
    if (isAutoRotating && !isHovered && items.length > 0) {
      interval = setInterval(() => {
        goToNext();
      }, autoRotateInterval);
    }
    return () => clearInterval(interval);
  }, [isAutoRotating, isHovered, items.length, goToNext]);

  if (!items || items.length === 0) return null;

  // Calculate the transform percentage
  // For 2 items per page, we shift by 50% per item index
  // For 1 item per page, we shift by 100% per item index
  // To keep it clean, we'll cap the index so it doesn't show empty space at the end
  const maxIndex = items.length - itemsPerPage;
  const safeIndex = Math.min(currentIndex, Math.max(0, maxIndex));
  const transformX = -(safeIndex * (100 / itemsPerPage));

  return (
    <div 
      className={`universal-slider ${isVisible ? 'animation' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='slider-container-main'>
        <button 
          className='slider-nav prev' 
          onClick={() => { setIsAutoRotating(false); goToPrev(); }} 
          disabled={currentIndex === 0}
          aria-label="Previous item"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>

        <div className='slider-viewport'>
          <div 
            className='slider-track'
            style={{ 
              transform: `translateX(${transformX}%)`,
              transition: 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          >
            {items.map((item, index) => (
              <div
                className={`slider-item ${index === currentIndex ? 'active' : ''}`}
                key={index}
                style={{ flex: `0 0 ${100 / itemsPerPage}%` }}
              >
                 <div className="card-wrapper">
                    {renderCard(item)}
                 </div>
              </div>
            ))}
          </div>
        </div>

        <button 
          className='slider-nav next' 
          onClick={() => { setIsAutoRotating(false); goToNext(); }} 
          disabled={currentIndex >= items.length - itemsPerPage}
          aria-label="Next item"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>

      <div className='slider-indicators'>
        {items.map((_, index) => (
          <button
            key={index}
            className={`indicator-dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => {
              setIsAutoRotating(false);
              setCurrentIndex(index);
            }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
